import { useRef } from 'react'
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material'
import AboutSplitBlock from './AboutSplitBlock'
import ScrollBoldText from './ScrollBoldText'
import { aboutMission, aboutVision } from '../../data/aboutContent'
import { useReducedMotion } from '../../hooks/useReducedMotion'
import { useVisionMissionScroll } from '../../hooks/useVisionMissionScroll'
import { BOARD_PIN_TOP_EXTRA_PX } from '../../utils/scrollAnimation'

const PIN_TOP_PADDING = BOARD_PIN_TOP_EXTRA_PX

const bodyTextSx = {
  fontSize: { xs: 18, sm: 24, md: 30, lg: 30 },
  lineHeight: 'normal',
  fontWeight: 400,
  color: '#000',
}

const labelSx = {
  fontSize: { xs: 22, md: 22 },
  lineHeight: 'normal',
  fontWeight: 400,
  color: '#000',
  flexShrink: 0,
}

const panelRowSx = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: 4,
  width: '100%',
  willChange: 'transform, opacity',
  backfaceVisibility: 'hidden',
}

const contentSx = {
  width: 1000,
  maxWidth: 1000,
  flexShrink: 0,
}

function VisionMissionBlocks() {
  return (
    <>
      <AboutSplitBlock label="Our Vision">
        <Typography sx={bodyTextSx}>{aboutVision}</Typography>
      </AboutSplitBlock>

      <AboutSplitBlock label="Our Mission">
        <Typography sx={bodyTextSx}>{aboutMission}</Typography>
      </AboutSplitBlock>
    </>
  )
}

function ScrollVisionMissionLayout({ layerRefs }) {
  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ visibility: 'hidden', pointerEvents: 'none' }} aria-hidden>
        <Box sx={panelRowSx}>
          <Typography component="h3" sx={labelSx}>
            Our Vision
          </Typography>
          <Box sx={contentSx}>
            <Typography sx={bodyTextSx}>{aboutVision}</Typography>
          </Box>
        </Box>
      </Box>

      <Box
        ref={(node) => {
          if (layerRefs) layerRefs.current.visionPanel = node
        }}
        sx={{
          ...panelRowSx,
          position: 'absolute',
          inset: 0,
        }}
      >
        <Typography component="h3" sx={labelSx}>
          Our Vision
        </Typography>
        <Box
          ref={(node) => {
            if (layerRefs) layerRefs.current.visionText = node
          }}
          sx={contentSx}
        >
          <ScrollBoldText text={aboutVision} />
        </Box>
      </Box>

      <Box
        ref={(node) => {
          if (layerRefs) layerRefs.current.missionPanel = node
        }}
        sx={{
          ...panelRowSx,
          position: 'absolute',
          inset: 0,
          opacity: 0,
        }}
      >
        <Typography component="h3" sx={labelSx}>
          Our Mission
        </Typography>
        <Box
          ref={(node) => {
            if (layerRefs) layerRefs.current.missionText = node
          }}
          sx={contentSx}
        >
          <ScrollBoldText text={aboutMission} />
        </Box>
      </Box>
    </Box>
  )
}

export default function AboutVisionMissionSection() {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'))
  const trackRef = useRef(null)
  const pinRef = useRef(null)
  const layerRefs = useRef({
    visionPanel: null,
    missionPanel: null,
    visionText: null,
    missionText: null,
  })
  const reducedMotion = useReducedMotion()

  const scrollActive = isDesktop && !reducedMotion
  useVisionMissionScroll(trackRef, pinRef, layerRefs, scrollActive)

  if (!scrollActive) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: '80px', md: 12, lg: '150px' } }}>
        <VisionMissionBlocks />
      </Box>
    )
  }

  return (
    <Box ref={trackRef} sx={{ position: 'relative', width: '100%' }}>
      <Box
        ref={pinRef}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          pt: { lg: `${PIN_TOP_PADDING}px` },
          boxSizing: 'border-box',
        }}
      >
        <ScrollVisionMissionLayout layerRefs={layerRefs} />
      </Box>
    </Box>
  )
}
